import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Subscription} from 'rxjs';
import {QuestionType} from '../../constants';
import {Question} from '../../models/underwriter.model';
import {GlobalQuestionService} from '../../services/global-question.service';
import {MiniFlowPlayerService} from '../../services/mini-flow-player-service';
import {MiniQuestionFlowSessionQuery} from '../state/mini-question-flow-session.query';
import { ComponentsService } from '../../services/components-service';
import { FlowPlayerService } from '../../services/flow-player.service';
import { MiniQuestionQuery } from '../state/mini-question.query';

declare var $: any;

export enum ContentTypes {
  Header = 1,
  Description = 2,
  Icon = 3,
  DeleteButton = 4
}

export class MiniFlowSavedItem {
  allow_delete: string;
  allow_edit: string;
  description_1: string;
  description_2: string;
  flow_id: number;
  icon: string;
  item_id: string;
  parameter: string;
  premium: any;
  product_class_id: number;
  session_id: string;
  questions: Question[];
}

@Component({
    selector: 'app-question-generic-miniflow-parent',
    templateUrl: './question-generic-miniflow-parent.component.html',
    styleUrls: ['./question-generic-miniflow-parent.component.scss'],
    standalone: false
})
export class QuestionGenericMiniflowParentComponent implements OnInit, OnDestroy {

  @Input() question: Question;
  @Output() questionAnswer = new EventEmitter<any>();
  @Output() disableButton = new EventEmitter<boolean>();
  session_details: any;
  childQuestion: Question;
  childrenQuestions: Question[] = [];
  item_ids: number[] = [];
  answeredQuestions: Question[] = [];
  max_item_id = '';
  item_id = '0';
  edit = false;
  parameters: any[] = [];
  showPremium = true;
  miniFlowSavedItems: MiniFlowSavedItem[] = [];
  updateResponseSubscription = new Subscription();

  constructor(
    public globalQuestionService: GlobalQuestionService,
    private sessionQuery: MiniQuestionFlowSessionQuery,
    private flowPlayer: FlowPlayerService,
    private componentsService: ComponentsService,
    private miniQuestionsQuery: MiniQuestionQuery,
    private miniFlowPlayerService: MiniFlowPlayerService) {
    this.updateResponseSubscription = this.miniFlowPlayerService.updateComponent()
      .subscribe((update: any) => {
        if (update) {
          this.loadSavedItems();
        }
      });
  }

  ngOnInit(): void {
    this.session_details = this.sessionQuery.getValue();
    this.loadSavedItems();
    if (this.question?.parameter && typeof this.question.parameter === 'string') {
      this.question.parameter = JSON.parse(this.question.parameter);
    }
    const premiumParam = Array.isArray(this.question.parameter) ? (this.question.parameter as any).find((p: any) => p.parameter === 'premium') : null;
    this.showPremium = premiumParam ? premiumParam.value !== '0' : false;
  }

  editItem(item_id: any): void {
    this.item_id = item_id;
    this.edit = true;
  }

  loadSavedItems(): void {
    this.miniFlowSavedItems = [];
    this.answeredQuestions = [];
    this.parameters = [];
    this.item_ids = [];
    this.item_id = '';
    this.max_item_id = '';

    this.globalQuestionService.getChildrenQuestions(this.question?.cent_aq_question_id)
      .subscribe((questions: Question[]) => {
        if (questions?.length) {
          this.childQuestion = Object.assign({}, questions[0]);
          questions?.forEach(q => {
            this.childrenQuestions.push(Object.assign({}, q));
          });
          if (this.childQuestion?.parameter) {
            this.parameters = JSON.parse(this.childQuestion?.parameter);
          }
        }
      });
    this.globalQuestionService.getMiniFlowItems(this.question)
      .subscribe(i => {
        this.miniFlowSavedItems = i;
        this.globalQuestionService.getResponses()
          .subscribe((responses: any[]) => {
            if (responses?.length) {
              this.edit = false;
              for (const response of responses) {
                if (response?.flow_id === this.childQuestion?.dependant_flow_id || response?.flow_id === this.question?.dependant_flow_id) {
                  this.answeredQuestions.push(Object.assign({}, response));
                }
              }
              if (this.miniFlowSavedItems?.length) {
                for (const item of this.miniFlowSavedItems) {
                  if (!item?.questions?.length) {
                    item.questions = [];
                  }
                  item.questions = this.answeredQuestions?.filter(q => q?.item_id === item?.item_id);
                  if (!this.item_ids?.length) {
                    this.item_ids = [];
                  }
                  this.item_ids.push(+item?.item_id);
                  this.max_item_id = Math.max.apply(null, this.item_ids).toString();
                  if (+item?.premium <= 0 && this.showPremium) {
                    this.globalQuestionService.getPremium(item?.product_class_id, item?.item_id, this.question?.product_id)
                      .subscribe({
                        next: (r: any[]) => {
                          if (r?.length) {
                            item.premium = r[0]?.premium;
                          }
                        }
                      });
                  }

                }
                if (this.miniFlowSavedItems?.every(g => g?.questions?.every(q => q?.answer))) {
                  this.disableButton.emit(false);
                }
              } else {
                this.max_item_id = '0';
              }
            } else {
              this.max_item_id = '0';
            }
          });
      });
  }

  getConcatenatedAddress(address: any): string {
    return `${address?.streetAddress}, ${address?.suburb}`;
  }

  getSummaryContent(content_type: ContentTypes, item_id: any): string {
    if (this.parameters?.length) {
      const summaryQuestionDesc1 = this.parameters?.find(x => x?.parameter === 'description_1');
      const summaryQuestionDesc2 = this.parameters?.find(x => x?.parameter === 'description_2');
      const deleteQuestionIdParameter = this.parameters?.find(x => x?.parameter === 'del');

      switch (content_type) {
        case ContentTypes.Header:
          let summaryQuestionDesc1Values = [];
          if (summaryQuestionDesc1?.value) {
            summaryQuestionDesc1Values = summaryQuestionDesc1?.value?.split('|');
          }
          if (summaryQuestionDesc1Values?.length) {
            let header = '';
            for (const hq of summaryQuestionDesc1Values) {
              const headerQuestion = this.answeredQuestions?.find(q => q?.cent_aq_question_id === +hq && q?.item_id === item_id);
              if (header) {
                header += ` ${headerQuestion?.answer}`;
              } else {
                header = headerQuestion?.answer;
              }
            }
            return header;
          }
          break;
        case ContentTypes.Description:
          if (summaryQuestionDesc2?.value) {
            const descriptionQuestion = this.answeredQuestions?.find(q => q?.cent_aq_question_id === +summaryQuestionDesc2?.value && q?.item_id === item_id);
            return descriptionQuestion?.answer;
          }
          break;
        case ContentTypes.Icon:
          break;
        case ContentTypes.DeleteButton:
          if (deleteQuestionIdParameter?.value) {
            const deleteParameterQuestion = this.answeredQuestions?.find(q => q?.cent_aq_question_id === deleteQuestionIdParameter?.value && q?.item_id === item_id);
            if (deleteParameterQuestion?.answer) {
              return deleteParameterQuestion?.answer;
            }
          }
          return '1';
      }
    }
    return '';
  }

  getAddress(group: any): any {
    const addressQuestion = group[1]?.find((q: any) => q?.cent_aq_question_type_id === QuestionType.address);
    const address: any = {};
    if (addressQuestion) {
      const answer = addressQuestion.answer.split(',');
      if (answer.length < 7) {
        address.streetAddress = answer[0];
        address.suburb = answer[1];
        address.province = answer[2];
        address.city = answer[3];
        address.postalCode = answer[4];
      } else {
        address.apartmentNo = answer[0];
        address.complex = answer[1];
        address.streetAddress = answer[2];
        address.suburb = answer[3];
        address.province = answer[4];
        address.city = answer[5];
        address.postalCode = answer[6];
      }
    }

    return address;
  }

  groupByItemId(questions: Question[]): any[] {
    const groups = [];
    if (questions?.length) {
      for (const q of questions) {
        if (!groups?.some(x => x?.item_id === q?.item_id)) {
          groups.push({item_id: q?.item_id, questions: [q]});
        } else {
          const index = groups?.findIndex(x => x?.item_id === q?.item_id);
          if (index > -1) {
            groups[index].questions.push(q);
          }
        }
      }
    }
    return groups;
  }

  submitResponse(event): void {
    this.childQuestion.answer = event?.answer;
    const responseQuestion = Object.assign({}, this.question);
    responseQuestion.answer = event?.answer;
    this.loadSavedItems();
    this.questionAnswer.emit(responseQuestion);
  }
  isNaN(t: any): boolean {
    return isNaN(t);
  }
  opemModalForm(): void{
    console.log(this.childQuestion, this.max_item_id);
    this.max_item_id = (+this.max_item_id + 1).toString();
     this.flowPlayer.startMiniFlow(this.childQuestion?.flow_id, this.childQuestion?.dependant_flow_id, this.session_details?.class_id, this.session_details?.category_id, this.session_details?.group_id, this.max_item_id, this.session_details?.session_id)
      .add(() => {
        const childQuestions = this.miniQuestionsQuery.getAll();
        console.log(childQuestions);
        this.componentsService.setMiniFormQuestion(childQuestions);
         $('#mini-form-modal').modal('show');
        
      });
    
   
  }
  ngOnDestroy(): void {
    this.updateResponseSubscription?.unsubscribe();
  }

}
